import React from "react";
import { updateOneFormula } from "../../api/petFormulaApi";
import { transformDataForApi, formatDateToMMDDYYYY } from "./utils.js";
import { OneFormulaHandlerParams, OneFormulaData, CoreBrandData, NameCompletionRowData } from "../../components/SettingsPage/types.js";
import ErrorNotification from "../../components/SettingsPage/ErrorNotification";
import SuccessNotification from "../../components/SettingsPage/SuccessNotification";
import { getAuth } from "firebase/auth";
import { NC_WEIGHT_KEYS } from "./nameCompletionHandlers";

interface NcOptions {
  ncEditingData: { [key: string]: Partial<NameCompletionRowData> };
  ncTableData: NameCompletionRowData[];
}

// A row is valid only when its percentage metric fields sum to 100
const validateRowSum = (rowData: Partial<OneFormulaData>): boolean => {
  const metricFields: (keyof OneFormulaData)[] = [
    'marketPenetration',
    'marketPenetrationOfClient',
    'avgCoreSkuClient',
    'comboPresence',
    'fixedComboPresence',
    'optionComboPresence',
    'nameAccuracy',
    'avgImagePresence',
  ];

  const sum = metricFields.reduce((total, field) => {
    const value = rowData[field];
    return total + (typeof value === 'number' ? value : 0);
  }, 0);

  // Tolerate small floating-point error (0.01%)
  return Math.abs(sum - 100) < 0.01;
};

// Handlers backing the ONE Performance formula editing flow
export const createOneFormulaHandlers = ({
  isEditingOneFormula,
  setIsEditingOneFormula,
  editingOneFormulaData,
  setEditingOneFormulaData,
  isEditingCoreBrands,
  editingCoreBrandsData,
  setEditingCoreBrandsData,
  setIsEditingCoreBrands,
  apiOneFormulaData,
  displayCoreBrandsData,
  setApiOneFormulaData,
  messageApi,
  userData,
  selectedWorkspace,
  persistDefaultRowDraft,
  setIsPipelineRunning,
  setIsNcPipelineRunning,
}: OneFormulaHandlerParams) => {
  // Resolve the editor's display name, preferring userData.name and falling back to Firebase Auth
  const getUserName = (): string => {
    if (userData?.name) {
      return userData.name;
    }

    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (currentUser?.displayName) {
        return currentUser.displayName;
      }
      if (currentUser?.email) {
        return currentUser.email;
      }
    } catch (error) {
      console.warn("[getUserName] Failed to get user from Firebase Auth:", error);
    }
    
    return "Unknown User";
  };

  // Validate every row's total against 100%, surfacing an error message when a row is off
  const validateOneFormulaData = (): boolean => {
    const currentOneData = apiOneFormulaData.length > 0 ? apiOneFormulaData : [];
    
    // Overlay the in-progress edits so validation runs against what the user sees
    const updatedOneData = currentOneData.map(item => ({
      ...item,
      ...editingOneFormulaData[item.key]
    }));

    const invalidRows = updatedOneData.filter(item => !validateRowSum(item));

    if (invalidRows.length > 0) {
      // Report the actual total of the first invalid row
      const firstInvalidRow = invalidRows[0];
      const metricFields: (keyof OneFormulaData)[] = [
        'marketPenetration',
        'marketPenetrationOfClient',
        'avgCoreSkuClient',
        'comboPresence',
        'fixedComboPresence',
        'optionComboPresence',
        'nameAccuracy',
        'avgImagePresence',
      ];
      const actualSum = metricFields.reduce((total, field) => {
        const value = firstInvalidRow[field];
        return total + (typeof value === 'number' ? value : 0);
      }, 0);
      const roundedSum = Math.round(actualSum * 100) / 100; // Round to 2 decimal places
      
      messageApi.error({
        key: "pet-formula-validation",
        content: React.createElement('div', { className: 'custom-error-message-wrapper' },
          React.createElement(ErrorNotification, {
            message: `The total should be 100%. Now you have ${roundedSum}%.`
          })
        ),
        duration: 5,
        icon: React.createElement('div'),
        className: 'pet-formula-error-message'
      });
      return false;
    }
    
    return true;
  };

  const handleEditAllOneFormula = async (ncOptions?: NcOptions) => {
    if (isEditingOneFormula) {
      // Save All - persist every edited row
      try {
        const currentOneData = apiOneFormulaData.length > 0 ? apiOneFormulaData : [];
        const currentCoreBrandsData = displayCoreBrandsData.length > 0 ? displayCoreBrandsData : [];

        // Overlay the in-progress edits
        const updatedOneData = currentOneData.map(item => ({
          ...item,
          ...editingOneFormulaData[item.key]
        }));

        // Belt-and-braces: the confirm modal only opens once validation passes
        if (!validateOneFormulaData()) {
          return;
        }

        messageApi.loading({
          key: "pet-formula-update",
          content: "Updating configuration data",
          duration: 0
        });

        // Core Brands saves together with the formula, so fold in its edits when that table is open
        let updatedCoreBrandsData = currentCoreBrandsData;
        if (isEditingCoreBrands && Object.keys(editingCoreBrandsData).length > 0) {
          updatedCoreBrandsData = currentCoreBrandsData.map(item => {
            const editedFields = editingCoreBrandsData[item.key] || {};
            const updatedItem = { ...item };

            // Apply all edited fields; empty string means user cleared the brand → store as "-"
            (Object.keys(editedFields) as (keyof CoreBrandData)[]).forEach(field => {
              const editedValue = editedFields[field];
              if (editedValue !== undefined) {
                updatedItem[field] = (editedValue === "" ? "-" : editedValue) as any;
              }
            });

            return updatedItem;
          });
        }

        const requestBody = transformDataForApi(updatedOneData, updatedCoreBrandsData);

        // Merge NC weights into request if provided (avoids a separate API call + version conflict)
        if (ncOptions) {
          const { ncEditingData, ncTableData } = ncOptions;
          requestBody.countries = (requestBody.countries as any[]).map((c: any) => {
            const ncRow = ncTableData.find((r) => r.key === c.country);
            if (!ncRow) return c;
            const merged = { ...ncRow, ...(ncEditingData[c.country] ?? {}) };
            const ncWeights: Record<string, number> = {};
            NC_WEIGHT_KEYS.forEach((k) => { ncWeights[k] = merged[k] as number; });
            return { ...c, name_completion_weights: ncWeights };
          });
          const defaultRow = ncTableData.find((r) => r.key === "default-value");
          if (defaultRow) {
            const mergedDefault = { ...defaultRow, ...(ncEditingData["default-value"] ?? {}) };
            const defaultWeights: Record<string, number> = {};
            NC_WEIGHT_KEYS.forEach((k) => { defaultWeights[k] = mergedDefault[k] as number; });
            (requestBody as any).default_name_completion_weights = defaultWeights;
          }
        }

        await updateOneFormula(requestBody, userData, selectedWorkspace);

        setIsPipelineRunning(true);
        // Merged save also kicked off the NC pipeline — start its polling loop so the
        // NC table refreshes on completion (parity with ONE Formula / Core Brands tables).
        if (ncOptions) {
          setIsNcPipelineRunning?.(true);
        }

        // Don't optimistically update table data — the pipeline hasn't completed yet,
        // so the active version is still the old one. The table should show the original
        // values until the pipeline succeeds and the data is refreshed from the server.

        // Clear editing state for CoreBrands if it was also being edited
        if (isEditingCoreBrands && Object.keys(editingCoreBrandsData).length > 0) {
          setEditingCoreBrandsData({});
          setIsEditingCoreBrands(false);
        }

        setEditingOneFormulaData({});
        setIsEditingOneFormula(false);

      } catch (error: any) {
        if (error.code === "400") {
          // Pick the message from the backend's detail field
          let errorMessage = "Request failed, please check that each of your markets has a core brand."; // default

          // axios nests the API detail under error.response.data.detail
          const errorDetail = error.response?.data?.detail;

          if (errorDetail && typeof errorDetail === 'string' && errorDetail.toLowerCase().includes('sum')) {
            errorMessage = "Request failed, please check that the sum of each market is 100%.";
          }

          console.log("[handleEditAllOneFormula] Error detail:", errorDetail);

          messageApi.error({
            key: "pet-formula-update",
            content: errorMessage,
            duration: 3
          });
        } else {
          messageApi.error({
            key: "pet-formula-update",
            content: `${error.message}`,
            duration: 3
          });
        }
        console.error("[handleEditAllOneFormula] Error:", error);
        throw error; // Re-throw so callers (e.g. handleConfirmSaveAll) can detect failure
      }
    } else {
      // Edit All - enter edit mode, seeding the draft from the saved values
      const currentData = apiOneFormulaData.length > 0 ? apiOneFormulaData : [];
      const initialEditingData: { [key: string]: Partial<OneFormulaData> } = {};

      currentData.forEach(item => {
        initialEditingData[item.key] = {
          marketPenetration: item.marketPenetration,
          marketPenetrationOfClient: item.marketPenetrationOfClient,
          avgCoreSkuClient: item.avgCoreSkuClient,
          comboPresence: item.comboPresence,
          fixedComboPresence: item.fixedComboPresence,
          optionComboPresence: item.optionComboPresence,
          nameAccuracy: item.nameAccuracy,
          avgImagePresence: item.avgImagePresence,
        };
      });

      setEditingOneFormulaData(initialEditingData);
      setIsEditingOneFormula(true);
    }
  };

  const handleCancelOneFormula = () => {
    // Cancel editing - discard changes and exit edit mode
    setEditingOneFormulaData({});
    setIsEditingOneFormula(false);
  };

  const handleInputChange = (key: string, field: keyof OneFormulaData, value: any) => {
    // set value to min 0 and max 100
    value = Math.max(0, Math.min(100, value || 0));
    setEditingOneFormulaData(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  // In Edit All mode the default values fill the inputs only; nothing is saved until the user saves
  const handleApplyDefaultToEditingRow = (key: string) => {
    const currentData = apiOneFormulaData.length > 0 ? apiOneFormulaData : [];
    const defaultRowKey = "default-value";
    const defaultRowFromApi = currentData.find(item => item.market === "Default Value");
    const targetRow = currentData.find(item => item.key === key);

    if (!defaultRowFromApi) {
      messageApi.error({
        content: "Default value row not found",
        duration: 3
      });
      return;
    }

    if (!targetRow) {
      messageApi.error({
        content: "Target row not found",
        duration: 3
      });
      return;
    }

    // Unsaved edits to the default row win over the saved values
    const editingDefaultRowData = editingOneFormulaData[defaultRowKey];
    const defaultRow = editingDefaultRowData 
      ? { ...defaultRowFromApi, ...editingDefaultRowData }
      : defaultRowFromApi;

    // Copy all metric values from default row to editing data
    const defaultValues = {
      marketPenetration: defaultRow.marketPenetration,
      marketPenetrationOfClient: defaultRow.marketPenetrationOfClient,
      avgCoreSkuClient: defaultRow.avgCoreSkuClient,
      comboPresence: defaultRow.comboPresence,
      fixedComboPresence: defaultRow.fixedComboPresence,
      optionComboPresence: defaultRow.optionComboPresence,
      nameAccuracy: defaultRow.nameAccuracy,
      avgImagePresence: defaultRow.avgImagePresence,
    };

    setEditingOneFormulaData(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...defaultValues
      }
    }));

    messageApi.success({
      key: `apply-default-to-editing-${key}`,
      content: `Default values applied to ${targetRow.market} editing fields.`,
      duration: 2
    });
  };

  // 🎯 Helper: resolve the current default values from editing state or API data
  const resolveDefaultValues = (key: string) => {
    const currentData = apiOneFormulaData.length > 0 ? apiOneFormulaData : [];
    const editedRowData = editingOneFormulaData[key];
    const sourceRow = currentData.find(item => item.key === key);
    if (!sourceRow) return null;

    return {
      marketPenetration: editedRowData?.marketPenetration ?? sourceRow.marketPenetration,
      marketPenetrationOfClient: editedRowData?.marketPenetrationOfClient ?? sourceRow.marketPenetrationOfClient,
      avgCoreSkuClient: editedRowData?.avgCoreSkuClient ?? sourceRow.avgCoreSkuClient,
      comboPresence: editedRowData?.comboPresence ?? sourceRow.comboPresence,
      fixedComboPresence: editedRowData?.fixedComboPresence ?? sourceRow.fixedComboPresence,
      optionComboPresence: editedRowData?.optionComboPresence ?? sourceRow.optionComboPresence,
      nameAccuracy: editedRowData?.nameAccuracy ?? sourceRow.nameAccuracy,
      avgImagePresence: editedRowData?.avgImagePresence ?? sourceRow.avgImagePresence,
    };
  };

  // 🎯 Save Default Value — persists to apiOneFormulaData + Redux draft.
  // Survives "Discard All" because it writes to the source of truth.
  const handleSaveDefaultValue = (key: string) => {
    const currentData = apiOneFormulaData.length > 0 ? apiOneFormulaData : [];
    const sourceRow = currentData.find(item => item.key === key);
    if (!sourceRow) {
      messageApi.error({ key: `pet-formula-save-default`, content: "Row not found", duration: 3 });
      return;
    }

    const newDefaultValues = resolveDefaultValues(key);
    if (!newDefaultValues) return;

    if (!validateRowSum(newDefaultValues)) {
      const metricFields: (keyof OneFormulaData)[] = [
        'marketPenetration', 'marketPenetrationOfClient', 'avgCoreSkuClient',
        'comboPresence', 'fixedComboPresence', 'optionComboPresence',
        'nameAccuracy', 'avgImagePresence',
      ];
      const actualSum = metricFields.reduce((total, field) => {
        const value = (newDefaultValues as any)[field];
        return total + (typeof value === 'number' ? value : 0);
      }, 0);
      const roundedSum = Math.round(actualSum * 100) / 100;

      messageApi.error({
        key: `pet-formula-save-default`,
        content: React.createElement('div', { className: 'custom-error-message-wrapper' },
          React.createElement(ErrorNotification, {
            message: `The total should be 100%. Now you have ${roundedSum}%.`
          })
        ),
        duration: 5,
        icon: React.createElement('div'),
        className: 'pet-formula-error-message'
      });
      return;
    }

    // Write to source of truth — survives discard
    const updatedRowData = {
      ...sourceRow,
      ...newDefaultValues,
      editBy: getUserName(),
      lastEditTime: formatDateToMMDDYYYY(new Date()),
    };
    const updatedDataset = currentData.map(item => item.key === key ? updatedRowData : item);
    setApiOneFormulaData(updatedDataset);
    persistDefaultRowDraft(updatedRowData);

    // Clear editing state for this row
    setEditingOneFormulaData((prev: any) => { const s = { ...prev }; delete s[key]; return s; });

    messageApi.success({
      key: `pet-formula-save-default`,
      content: React.createElement('div', { className: 'custom-success-message-wrapper' },
        React.createElement(SuccessNotification, {
          message: "Default values saved. Use 'Apply to All' to copy to all markets."
        })
      ),
      duration: 3,
      icon: React.createElement('div'),
      className: 'pet-formula-success-message'
    });
  };

  // 🎯 Apply to All — copies default values into all market rows' editing data.
  // Revertable on discard because it only writes to editingOneFormulaData.
  const handleApplyDefaultToAll = () => {
    const currentData = apiOneFormulaData.length > 0 ? apiOneFormulaData : [];
    const defaultRowKey = "default-value";

    // Use the saved default row from apiOneFormulaData (already persisted by Save Default)
    // or fall back to editing state if the user hasn't saved yet
    const defaultRowFromApi = currentData.find(item => item.market === "Default Value");
    if (!defaultRowFromApi) {
      messageApi.error({ key: `pet-formula-apply-all`, content: "Default value row not found", duration: 3 });
      return;
    }

    // Prefer editing values (if user changed default but hasn't saved yet), else use saved values
    const editingDefault = editingOneFormulaData[defaultRowKey];
    const defaultValues = {
      marketPenetration: editingDefault?.marketPenetration ?? defaultRowFromApi.marketPenetration,
      marketPenetrationOfClient: editingDefault?.marketPenetrationOfClient ?? defaultRowFromApi.marketPenetrationOfClient,
      avgCoreSkuClient: editingDefault?.avgCoreSkuClient ?? defaultRowFromApi.avgCoreSkuClient,
      comboPresence: editingDefault?.comboPresence ?? defaultRowFromApi.comboPresence,
      fixedComboPresence: editingDefault?.fixedComboPresence ?? defaultRowFromApi.fixedComboPresence,
      optionComboPresence: editingDefault?.optionComboPresence ?? defaultRowFromApi.optionComboPresence,
      nameAccuracy: editingDefault?.nameAccuracy ?? defaultRowFromApi.nameAccuracy,
      avgImagePresence: editingDefault?.avgImagePresence ?? defaultRowFromApi.avgImagePresence,
    };

    if (!validateRowSum(defaultValues)) {
      messageApi.error({
        key: `pet-formula-apply-all`,
        content: "Default values don't sum to 100%. Save valid defaults first.",
        duration: 5
      });
      return;
    }

    // Apply to all rows' editing data (revertable on discard)
    setEditingOneFormulaData((prev: any) => {
      const newState: { [k: string]: any } = {};
      for (const rowKey of Object.keys(prev)) {
        newState[rowKey] = {
          ...prev[rowKey],
          ...defaultValues,
        };
      }
      return newState;
    });

    messageApi.success({
      key: `pet-formula-apply-all`,
      content: React.createElement('div', { className: 'custom-success-message-wrapper' },
        React.createElement(SuccessNotification, {
          message: "Default values applied to all markets."
        })
      ),
      duration: 3,
      icon: React.createElement('div'),
      className: 'pet-formula-success-message'
    });
  };

  return {
    handleEditAllOneFormula,
    handleCancelOneFormula,
    validateOneFormulaData,
    handleInputChange,
    handleApplyDefaultToEditingRow,
    handleSaveDefaultValue,
    handleApplyDefaultToAll,
  };
};
